import { derived, get, type Readable } from 'svelte/store';
import type { ValueOf } from 'type-fest';

import type { AuthContext, GetRPCOptions, UserInfo } from '$lib/auth';
import { AuthStatus, useAuthContext } from '$lib/auth';

import { type APIServiceClients, useClient } from './client';

/** Re-export to avoid conflict with common variable name `queryOptions`. */
export {
  infiniteQueryOptions as createInfiniteQueryOptions,
  queryOptions as createQueryOptions,
} from '$lib/api/tanstack-query';

/** Base options used by queries. */
export interface BaseQueryOptions<ClientT extends ValueOf<APIServiceClients>> {
  /** The RPC client to use. */
  client: ClientT;
  /** Get request headers. */
  getRPCOptions: GetRPCOptions;
  /** Whether the query should be enabled. */
  enabled: boolean;
  /** Information about the currently authed user. */
  user: UserInfo | undefined;
}

/**
 * Get base options from a client and auth context for a query that requires auth.
 *
 * For use in +layout and +page `load` functions without store reactivity.
 * In a component context, use `useAuthedQueryOptions` instead.
 */
export const getAuthedQueryOptions = <
  ClientT extends ValueOf<APIServiceClients>,
>(
  client: ClientT,
  authContext: AuthContext
): BaseQueryOptions<ClientT> => {
  const state = get(authContext.authState);
  return {
    client,
    getRPCOptions: authContext.getRPCOptions,
    enabled: state.status === AuthStatus.LoggedIn,
    user: state.status === AuthStatus.LoggedIn ? state.user : undefined,
  };
};

/**
 * Get base options from `useClient` and `useAuthContext` for a query that requires auth.
 *
 * For use in components to ensure proper reactivity.
 * In `load` functions, use `getAuthedQueryOptions` instead.
 */
export const useAuthedQueryOptions = <
  ClientNameT extends keyof APIServiceClients,
>(
  clientType: ClientNameT
): Readable<BaseQueryOptions<APIServiceClients[ClientNameT]>> => {
  const client = useClient(clientType);
  const authContext = useAuthContext();

  return derived(authContext.authState, ($authState) => ({
    client,
    getRPCOptions: authContext.getRPCOptions,
    enabled: $authState.status === AuthStatus.LoggedIn,
    user:
      $authState.status === AuthStatus.LoggedIn ? $authState.user : undefined,
  }));
};
